import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  useTheme,
  Link as MuiLink,
} from '@mui/material';
import { Link } from 'react-router-dom';

const DPA: React.FC = () => {
  const theme = useTheme();
  const lastUpdated = 'August 27, 2026';

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
          pt: { xs: 4, md: 6 },
          pb: { xs: 4, md: 6 },
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              mb: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Data Processing Agreement (DPA)
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Last updated: {lastUpdated}
          </Typography>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper elevation={0} sx={{ p: { xs: 3, md: 6 }, border: `1px solid ${theme.palette.divider}` }}>

          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            This Data Processing Agreement ("DPA") forms part of the{' '}
            <MuiLink component={Link} to="/legal/terms">
              Terms of Service
            </MuiLink>{' '}
            between:
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            <strong>Customer</strong> (the "Controller")<br />
            and<br />
            <strong>Jakatech LLC</strong>, a Texas limited liability company d/b/a Tier One Content ("Jakatech" or "Processor").
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            This DPA applies where Customer processes Personal Data subject to the General Data Protection Regulation
            ("GDPR") and Jakatech processes such Personal Data on Customer's behalf.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            1. Definitions
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>"Personal Data"</strong> has the meaning given in GDPR Article 4(1).
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>"Processing"</strong> has the meaning given in GDPR Article 4(2).
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>"Subprocessor"</strong> means any third party engaged by Processor to process Personal Data.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>"Standard Contractual Clauses"</strong> or <strong>"SCCs"</strong> means the European Commission
                Implementing Decision (EU) 2021/914.
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            All other capitalized terms have the meaning given in the Terms of Service.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            2. Scope and Role of the Parties
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            <strong>2.1</strong> Customer acts as Data Controller for Personal Data contained within uploaded content
            and related metadata.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            <strong>2.2</strong> Jakatech acts as Data Processor, processing Personal Data solely on documented
            instructions from Customer, including as set forth in the Terms of Service.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            <strong>2.3</strong> For account registration, billing, and platform administration data, Jakatech acts
            as an independent Data Controller (outside the scope of this DPA).
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            3. Processor Obligations
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Jakatech shall:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Process Personal Data only on documented instructions from Customer.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Ensure personnel are subject to confidentiality obligations.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Implement appropriate technical and organizational measures ("TOMs") as described in Annex II.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Assist Customer in responding to Data Subject Requests.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Assist Customer with data protection impact assessments where required.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Notify Customer without undue delay after becoming aware of a Personal Data Breach.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Delete or return Personal Data upon termination, subject to applicable law.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Make available information necessary to demonstrate compliance.
              </Typography>
            </li>
          </ul>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            4. Subprocessors
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            <strong>4.1</strong> Customer authorizes Jakatech to engage Subprocessors listed in Annex III.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            <strong>4.2</strong> Jakatech shall:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Impose data protection obligations on Subprocessors equivalent to this DPA.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Remain liable for Subprocessor performance.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Notify Customer of material Subprocessor changes (via website update or email).
              </Typography>
            </li>
          </ul>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            5. International Transfers
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            <strong>5.1</strong> Where Personal Data is transferred outside the European Economic Area (EEA), such
            transfer shall be governed by:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                The EU Standard Contractual Clauses (Module 2: Controller to Processor), incorporated by reference; and/or
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                The EU–US Data Privacy Framework (where applicable).
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            <strong>5.2</strong> For the purposes of the SCCs:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Customer is the "Data Exporter."
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Jakatech is the "Data Importer."
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Annex I, II, and III of this DPA serve as the Annexes to the SCCs.
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            <strong>5.3</strong> The optional docking clause (Clause 7) applies.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            <strong>5.4</strong> The governing law for the SCCs shall be the law of Ireland (or another EU Member
            State where required).
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            6. Security and Breach Notification
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            <strong>6.1</strong> Jakatech shall implement the Technical and Organizational Measures described in Annex II.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            <strong>6.2</strong> In the event of a confirmed Personal Data Breach, Jakatech shall:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Notify Customer without undue delay;
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Provide available details regarding nature, scope, and mitigation steps;
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Cooperate reasonably with Customer.
              </Typography>
            </li>
          </ul>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            7. Data Subject Rights
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Jakatech shall, taking into account the nature of processing, assist Customer by appropriate technical
            and organizational measures to fulfill obligations relating to:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Access
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Rectification
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Erasure
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Restriction
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Data portability
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Objection
              </Typography>
            </li>
          </ul>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            8. Deletion and Return of Data
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Upon termination of the Service:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Customer may request deletion or return of Personal Data within 30 days.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                After that period, Jakatech may delete Customer Data in accordance with retention policies.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Backup systems may retain encrypted data for limited additional periods.
              </Typography>
            </li>
          </ul>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            9. Liability
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Liability shall be governed by the Terms of Service, except where otherwise required by GDPR or the SCCs.
          </Typography>

          <Divider sx={{ my: 6 }} />

          {/* ANNEX I */}
          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            ANNEX I – DETAILS OF PROCESSING
          </Typography>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            A. Subject Matter
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Provision of hosted content-sharing and collaboration platform.
          </Typography>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            B. Duration
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            For the duration of the Terms of Service plus applicable retention periods.
          </Typography>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            C. Nature and Purpose
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Hosting
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Storage
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Indexing
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Tagging
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Search optimization
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                AI-powered content analysis and description generation
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Semantic vector embedding and search indexing
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Secure transmission
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Access control
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            D. Categories of Data Subjects
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Customer employees
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Authorized users
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Business partners
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                End customers (if uploaded by Customer)
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            E. Categories of Personal Data
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Name
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Email address
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Organization details
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Uploaded content containing personal data
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Usage metadata
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                IP address
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Sensitive personal data is not intentionally processed and should not be uploaded without lawful authorization.
          </Typography>

          <Divider sx={{ my: 6 }} />

          {/* ANNEX II */}
          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            ANNEX II – TECHNICAL & ORGANIZATIONAL MEASURES (TOMs)
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Jakatech may implement measures including:
          </Typography>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            1. Encryption
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                TLS encryption in transit
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Encryption at rest (cloud storage and databases)
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            2. Access Controls
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Role-based access control (RBAC)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Least privilege principles
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Multi-factor authentication for administrative access
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            3. Network Security
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Firewalls
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Network segmentation
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Cloud provider security controls
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            4. Logging & Monitoring
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Audit logging
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Access logs
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Error monitoring
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            5. Vulnerability Management
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Regular dependency updates
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Patch management
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Cloud infrastructure updates
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            6. Data Minimization
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Limited metadata collection
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Retention controls
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Scoped log retention
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            7. Business Continuity
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Cloud-based redundancy
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Automated backups
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Disaster recovery procedures
              </Typography>
            </li>
          </ul>

          <Divider sx={{ my: 6 }} />

          {/* ANNEX III */}
          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            ANNEX III – AUTHORIZED SUBPROCESSORS
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Effective Date: {lastUpdated}
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Jakatech LLC d/b/a Tier One Content uses the following subprocessors in connection with the provision
            of the Service.
          </Typography>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            1. Google LLC
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Service:</strong> Google Cloud Platform (Cloud Run, Secret Manager, Vertex AI / Gemini)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Purpose:</strong> Infrastructure hosting, compute execution, secrets management, and AI-powered content analysis and description generation (Vertex AI / Gemini Flash)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Categories of Data:</strong> Account data, metadata, service logs, application processing data, content text and file data submitted for AI analysis
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Location:</strong> United States and optional EU regions
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Transfer Mechanism:</strong> Standard Contractual Clauses (SCCs) and EU–US Data Privacy Framework
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Role:</strong> Subprocessor
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            2. MongoDB, Inc.
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Service:</strong> MongoDB Atlas
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Purpose:</strong> Managed database services
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Categories of Data:</strong> Account data, content metadata, application data
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Location:</strong> United States
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Transfer Mechanism:</strong> Standard Contractual Clauses (SCCs)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Role:</strong> Subprocessor
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            3. Cloudflare, Inc.
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Services:</strong> R2 Object Storage, Cloudflare Pages, Content Delivery Network (CDN), DDoS Protection
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Purpose:</strong> Content storage, static asset hosting, traffic routing, edge caching, security
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Categories of Data:</strong> Uploaded content, IP addresses, request metadata
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Location:</strong> United States and global edge network
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Transfer Mechanism:</strong> Standard Contractual Clauses (SCCs) and EU–US Data Privacy Framework
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Role:</strong> Subprocessor
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            4. Resend, Inc.
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Service:</strong> Transactional Email Delivery
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Purpose:</strong> Outbound email communications
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Categories of Data:</strong> Email address, message content, metadata, IP address
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Location:</strong> United States
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Transfer Mechanism:</strong> Standard Contractual Clauses (SCCs)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Role:</strong> Subprocessor
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            5. Microsoft Corporation
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Service:</strong> Microsoft 365 / Exchange Online
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Purpose:</strong> Inbound email hosting (support communications)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Categories of Data:</strong> Support emails, attachments, metadata
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Location:</strong> United States
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Transfer Mechanism:</strong> SCCs and EU–US Data Privacy Framework
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Role:</strong> Subprocessor
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            6. Stripe, Inc.
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Service:</strong> Payment Processing
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Purpose:</strong> Subscription billing and transaction processing
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Categories of Data:</strong> Billing name, billing address, email, payment method details, transaction metadata
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Location:</strong> United States
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Transfer Mechanism:</strong> EU–US Data Privacy Framework
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Role:</strong> Independent Controller
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            7. Google LLC
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Service:</strong> Google Authentication (OAuth)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Purpose:</strong> User authentication
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Categories of Data:</strong> Email address, name, profile identifier
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Location:</strong> United States
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Transfer Mechanism:</strong> EU–US Data Privacy Framework
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Role:</strong> Independent Controller
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            8. Microsoft Corporation
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Service:</strong> Microsoft Authentication (OAuth)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Purpose:</strong> User authentication
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Categories of Data:</strong> Email address, name, profile identifier
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Location:</strong> United States
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Transfer Mechanism:</strong> EU–US Data Privacy Framework
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Role:</strong> Independent Controller
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            9. Google LLC
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Service:</strong> Google Analytics
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Purpose:</strong> Website and application usage analytics (activated only with user consent)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Categories of Data:</strong> IP address (anonymized where applicable), usage data, device metadata
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Location:</strong> United States
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Transfer Mechanism:</strong> EU–US Data Privacy Framework and SCCs
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Role:</strong> Subprocessor (analytics provider)
              </Typography>
            </li>
          </ul>
          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            10. Twilio Inc. (if enabled)
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Service:</strong> SMS Delivery (if enabled)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Purpose:</strong> Transactional SMS notifications
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Categories of Data:</strong> Phone number, message content, delivery metadata
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Location:</strong> United States
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Transfer Mechanism:</strong> SCCs and EU–US Data Privacy Framework
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Role:</strong> Subprocessor
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            11. OpenAI, Inc.
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Service:</strong> OpenAI API (GPT-4o)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Purpose:</strong> AI-powered content description and metadata generation
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Categories of Data:</strong> Text extracted from uploaded content files (documents, presentations, etc.)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Location:</strong> United States
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Transfer Mechanism:</strong> SCCs and EU–US Data Privacy Framework
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Role:</strong> Subprocessor
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            12. Voyage AI, Inc.
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Service:</strong> Voyage AI Embedding API (voyage-3)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Purpose:</strong> Semantic vector embedding for content search indexing
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Categories of Data:</strong> Text extracted from uploaded content files submitted for embedding generation
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Location:</strong> United States
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Transfer Mechanism:</strong> SCCs and EU–US Data Privacy Framework
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Role:</strong> Subprocessor
              </Typography>
            </li>
          </ul>

          <Divider sx={{ my: 6 }} />

          {/* Execution */}
          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            Execution
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            This DPA is incorporated into and governed by the{' '}
            <MuiLink component={Link} to="/legal/terms">
              Terms of Service
            </MuiLink>
            .
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Acceptance of the Terms constitutes acceptance of this DPA.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Enterprise customers may request a countersigned version upon request.
          </Typography>

          <Divider sx={{ my: 5 }} />

          <Typography variant="body2" color="text.secondary">
            This Data Processing Agreement was last updated on {lastUpdated}.
          </Typography>
        </Paper>
      </Container>
    </>
  );
};

export default DPA;
